import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import TextAlign from '@tiptap/extension-text-align';
import { TextStyle } from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import Typography from '@tiptap/extension-typography';
import { ThemeColors } from 'app/domain/constants/ThemeColors';

@Component({
  selector: 'ebo-ebo-wysiwyg',
  templateUrl: './ebo-wysiwyg.component.html',
  styleUrls: ['./ebo-wysiwyg.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => EboWysiwygComponent),
      multi: true
    }
  ]
})
export class EboWysiwygComponent implements OnInit, AfterViewInit, OnDestroy, ControlValueAccessor {  @Input() placeholder = 'Write something...';
  @Input() characterLimit = 0;
  @Input() label = '';
  @Input() required = false;

  @ViewChild('editorContainer', { static: true }) editorContainer!: ElementRef<HTMLDivElement>;

  editor!: Editor;
  value = '';
  isDisabled = false;

  headingLevels = [1, 2, 3, 4, 5, 6];

  fontColors: { label: string; value: string }[] = [];
  highlightColors: { label: string; value: string }[] = [];

  private onChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.fontColors = [
      { label: 'Default', value: '' },
      { label: 'Black', value: ThemeColors.text },
      { label: 'Grey 900', value: ThemeColors.grey[900] },
      { label: 'Grey 700', value: ThemeColors.grey[700] },
      { label: 'Grey 500', value: ThemeColors.grey[500] },
      { label: 'Purple 500', value: ThemeColors.purple[500] },
      { label: 'Purple 300', value: ThemeColors.purple[300] },
      { label: 'Purple 100', value: ThemeColors.purple[100] },
      { label: 'L.Purple 500', value: ThemeColors.lightPurple[500] },
      { label: 'L.Purple 300', value: ThemeColors.lightPurple[300] },
      { label: 'Red 500', value: ThemeColors.red[500] },
      { label: 'Red 300', value: ThemeColors.red[300] },
      { label: 'Green 500', value: ThemeColors.green[500] },
      { label: 'Green 300', value: ThemeColors.green[300] },
      { label: 'Yellow 500', value: ThemeColors.yellow[500] },
      { label: 'Yellow 300', value: ThemeColors.yellow[300] },
      { label: 'Blue 500', value: ThemeColors.lightBlue[500] },
      { label: 'Blue 300', value: ThemeColors.lightBlue[300] },
      { label: 'Success', value: ThemeColors.semantic.success },
      { label: 'Warning', value: ThemeColors.semantic.warning },
      { label: 'Error', value: ThemeColors.semantic.error },
      { label: 'Info', value: ThemeColors.semantic.info }
    ];
    this.highlightColors = [
      { label: 'Purple', value: ThemeColors.purple[50] },
      { label: 'Light Purple', value: ThemeColors.lightPurple[50] },
      { label: 'Red', value: ThemeColors.red[50] },
      { label: 'Green', value: ThemeColors.green[50] },
      { label: 'Yellow', value: ThemeColors.yellow[50] },
      { label: 'Blue', value: ThemeColors.lightBlue[50] },
      { label: 'Grey', value: ThemeColors.grey[50] },
      { label: 'Purple 100', value: ThemeColors.purple[100] },
      { label: 'Green 100', value: ThemeColors.green[100] },
      { label: 'Yellow 100', value: ThemeColors.yellow[100] },
      { label: 'Blue 100', value: ThemeColors.lightBlue[100] }
    ];
  }


  ngAfterViewInit(): void {
    this.editor = new Editor({
      element: this.editorContainer.nativeElement,
      extensions: [
        StarterKit.configure({
          link: { openOnClick: false },
          underline: {}
        }),
        TextAlign.configure({ types: ['heading', 'paragraph'] }),
        TextStyle as any,
        Color,
        Highlight.configure({ multicolor: true }),
        Placeholder.configure({ placeholder: this.placeholder }),
        ...(this.characterLimit > 0
          ? [CharacterCount.configure({ limit: this.characterLimit })]
          : [CharacterCount]),
        Subscript,
        Superscript,
        Typography
      ],
      content: this.value,
      onTransaction: () => {
        // Force Angular change detection so toolbar active states stay in sync
        this.cdr.detectChanges();
      },
      onUpdate: ({ editor }) => {
        const html = editor.getHTML();
        this.value = html;
        this.onChange(html);
        this.onTouched();
      }
    });

    if (this.isDisabled) {
      this.editor.setEditable(false);
    }
  }

  ngOnDestroy(): void {
    this.editor?.destroy();
  }

  // ControlValueAccessor
  writeValue(value: string): void {
    this.value = value || '';
    if (this.editor) {
      this.editor.commands.setContent(this.value, false as any);
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.editor?.setEditable(!isDisabled);
  }

  // Toolbar helpers
  get characterCount(): number {
    return this.editor?.storage?.characterCount?.characters() ?? 0;
  }

  get wordCount(): number {
    return this.editor?.storage?.characterCount?.words() ?? 0;
  }

  setHeading(level: number): void {
    this.editor.chain().focus().toggleHeading({ level: level as any }).run();
  }

  setFontColor(color: string): void {
    if (!color) {
      this.editor.chain().focus().unsetColor().run();
    } else {
      this.editor.chain().focus().setColor(color).run();
    }
  }

  setHighlight(color: string): void {
    this.editor.chain().focus().toggleHighlight({ color }).run();
  }

  setLink(): void {
    const previousUrl = this.editor.getAttributes('link')['href'];
    const url = window.prompt('URL', previousUrl);
    if (url === null) return;
    if (url === '') {
      this.editor.chain().focus().extendMarkRange('link').unsetLink().run();
    } else {
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }
  }

  isActive(name: string, attrs?: Record<string, any>): boolean {
    return this.editor?.isActive(name, attrs) ?? false;
  }

  isAligned(align: string): boolean {
    return this.editor?.isActive({ textAlign: align }) ?? false;
  }
}
